import platform, {
  InviteAsyncOptions,
  ShareAsyncOptions,
  analytics,
} from '@play-co/gcinstant';
import { CreativeAsset } from '../creatives/core';
import { CreativeText } from '../replicant/creatives/text';
import { makePayload } from './analytics';
import StateObserver from '../StateObserver';
import { NewSocialGraphInteraction } from '@play-co/replicant';
import { captureGenericError } from './sentry';
import { getStrings } from './i18n/i18n';
import { encodePacket } from '../replicant/utils/encoder';
import { Social } from '../replicant/social';
import { UpdateAsyncOptions } from '@play-co/gcinstant/build/esm/Platform';

type TemplatesIDs = 'dynamic_square' | 'dynamic_rect';

type UiOpts = {
  ignoreDailyInviteLimit?: boolean;
  ignoreDailyInviteeCount?: boolean;
  totalSelectionLimit?: number; // Default 10
  friendsSorting?: 'random' | 'alphabet' | string[];
  friendCountLimit?: number;
};

export const defaultUiOpts: UiOpts = {
  ignoreDailyInviteLimit: true,
  ignoreDailyInviteeCount: true,
  totalSelectionLimit: 10,
  friendsSorting: 'random',
  friendCountLimit: Number.MAX_SAFE_INTEGER,
};

export async function inviteAsync(args: {
  creativeAsset?: CreativeAsset;
  creativeText?: CreativeText;
  text?: string;
  cta?: string;
  template?: TemplatesIDs;

  data: {
    feature: string;
    $subFeature: string;
    $sharingID?: string;
    $sharingTimestamp?: string;
  };

  receiverIDs?: string[];
  sortedIDs?: string[];
  confirmPopup?: boolean;
  debug?: boolean;
}): Promise<string[]> {
  const opts: InviteAsyncOptions = {
    image: args.creativeAsset?.image,
    template: args.template ?? 'dynamic_square',
    text: args.text || args.creativeText?.text,
    cta: args.cta || args.creativeText?.cta,

    data: {
      ...args.data,
      ...makePayload('INVITE'),

      $creativeAssetID: args.creativeAsset?.id,
      $creativeTextID: args.creativeText?.id,
    },
  };

  opts.uiOpts = {
    ...defaultUiOpts,
    ...getStrings().platform[process.env.PLATFORM]?.share,
    confirmPopup: args.confirmPopup,
  };
  if (args.sortedIDs) {
    opts.uiOpts.friendsSorting = args.sortedIDs;
  }

  const now = StateObserver.now();
  if (args.receiverIDs) {
    opts.receiverIDs = args.receiverIDs;
  } else {
    const friendIds = platform.socialFriends
      .map(({ id }) => id)
      .filter((id) => !platform.friendIds.includes(id));
    const { user } = StateObserver.getState();
    const { getExcludedFriendsForInvite } = Social.getters(user);
    opts.uiOpts.excludePlayerIDs = getExcludedFriendsForInvite(friendIds, now);
  }

  if (args.debug) {
    opts.uiOpts.includePlayers = true;
    opts.uiOpts.excludePlayerIDs = [];
  }

  const receivers = await platform.inviteAsync(opts);
  if (!receivers) {
    return undefined;
  }

  const { receiverIDs } = receivers;
  const newInvitees = receiverIDs.filter(
    (receiverId) => !platform.friendIds.includes(receiverId),
  );
  await StateObserver.invoke.recordInvite(newInvitees);

  // Track interactions
  const interactions: NewSocialGraphInteraction[] = newInvitees.map(
    (receiverId) => ({
      meta: {}, // TODO attach any interaction metadata properties we're interested in
      receiverId: encodePacket(receiverId),
      type: 'invite', // TODO figure out interaction types
      timestamp: now,
    }),
  );

  if (interactions.length > 0) {
    await StateObserver.replicant.socialGraph
      .trackInteractions(interactions)
      .catch((error) => {
        console.warn('Tracking social interactions failed', error);
        captureGenericError('Tracking social interactions failed', error);
      });
  }

  return receiverIDs;
}

/**
 * Send individual U2U message
 */
export async function updateAsync(args: {
  creativeAsset?: CreativeAsset;
  creativeText?: CreativeText;
  text?: string;
  cta?: string;
  template?: TemplatesIDs;

  data: {
    feature: string;
    $subFeature: string;
    $sharingID?: string;
    $sharingTimestamp?: string;
  };

  receiverID: string;
  confirmPopup?: boolean;
  debug?: boolean;
}): Promise<void> {
  const opts: UpdateAsyncOptions = {
    image: args.creativeAsset?.image,
    template: args.template ?? 'dynamic_square',
    text: args.text || args.creativeText?.text,
    cta: args.cta || args.creativeText?.cta,

    receiver: {
      id: args.receiverID,
    },

    data: {
      ...args.data,
      ...makePayload('UPDATE'),

      $creativeAssetID: args.creativeAsset?.id,
      $creativeTextID: args.creativeText?.id,
    },
  };

  opts.uiOpts = {
    ...defaultUiOpts,
    ...getStrings().platform[process.env.PLATFORM]?.share,
    confirmPopup: args.confirmPopup,
  };

  // Change context to target user context
  await platform.createContextAsync(args.receiverID, args.data, opts.uiOpts);

  // Send messages to sended context
  await platform.updateAsync(opts);

  // Check if user is playing friend, and exit
  // socialGraph only for non playing friends
  if (platform.friendIds.includes(args.receiverID)) return;

  // Track interactions with non-playing-friends
  const interaction: NewSocialGraphInteraction = {
    meta: {}, // TODO attach any interaction metadata properties we're interested in
    receiverId: encodePacket(args.receiverID),
    type: 'update', // TODO figure out interaction types
    timestamp: StateObserver.now(),
  };

  await StateObserver.replicant.socialGraph
    .trackInteractions([interaction])
    .catch((error) => {
      console.warn('Tracking social interactions failed', error);
      captureGenericError('Tracking social interactions failed', error);
    });
}

/**
 * Send share message
 */
export async function shareAsync(args: {
  creativeAsset?: CreativeAsset;
  creativeText?: CreativeText;
  text?: string;
  cta?: string;
  template?: TemplatesIDs;

  data: {
    feature: string;
    $subFeature: string;
    $sharingID?: string;
    $sharingTimestamp?: string;
  };

  receiverIDs?: string[];
  switchContext?: boolean;

  confirmPopup?: boolean;
  debug?: boolean;
}): Promise<string[]> {
  const opts: ShareAsyncOptions = {
    image: args.creativeAsset?.image,
    template: args.template ?? 'dynamic_square',
    text: args.text || args.creativeText?.text,
    cta: args.cta || args.creativeText?.cta,
    switchContext: args.switchContext ?? true,

    receiverIDs: args.receiverIDs,

    data: {
      ...args.data,
      ...makePayload('SHARE'),

      $creativeAssetID: args.creativeAsset?.id,
      $creativeTextID: args.creativeText?.id,
    },
  };

  opts.uiOpts = {
    ...defaultUiOpts,
    ...getStrings().platform[process.env.PLATFORM]?.share,
    confirmPopup: args.confirmPopup,
  };

  // Share message to user
  const sent = (await platform.shareAsync(opts)) as { receiverIDs: string[] };

  // Track interactions for non playing friends
  const interactions: NewSocialGraphInteraction[] = sent.receiverIDs
    // SocialGraph only for non playing friends
    .filter((id) => {
      return !platform.friendIds.includes(id);
    })
    .map((receiverId) => ({
      meta: {}, // TODO attach any interaction metadata properties we're interested in
      receiverId: encodePacket(receiverId),
      type: 'share', // TODO figure out interaction types
      timestamp: StateObserver.now(),
    }));

  if (interactions.length > 0) {
    await StateObserver.replicant.socialGraph
      .trackInteractions(interactions)
      .catch((error) => {
        console.warn('Tracking social interactions failed', error);
        captureGenericError('Tracking social interactions failed', error);
      });
  }

  return sent.receiverIDs;
}

/**
 * Check the entry data payload if has an inviter id, and if so, notify the inviter that the user joined
 */
export async function notifyInvitedUserJoined() {
  // Find out the id of the player that shared the link to the user
  const inviterId = platform.entryData?.playerID;

  // Skip if inviter id is missing
  if (!inviterId) return;

  // Use current time as timestamp for the invite
  const timeSent = Date.now();

  // Invoke the action that will post a message to the sender state
  void StateObserver.invoke.notifyInvitedUserJoined({
    inviterId,
    timeSent,
  });
}
